import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Typography, Button, Table, Modal, Space } from "antd";
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

import {
  getSystemNotificationListRequest,
  deleteAllSystemNotificationsRequest,
  retrySystemNotificationRequest,
} from "providers/SystemNotificationProvider/slice";
import SystemNotificationColumn from "components/Columns/SystemNotificationColumn";
import Helper from "utils/helpers";
import "./styles.less";

const SystemNotificationList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const notificationList = useSelector((state) => {
    console.log("System notification state:", state.systemNotification);
    return state.systemNotification.notificationList;
  });
  const pagination = useSelector((state) => 
    state.systemNotification.pagination
  );
  const isLoading = useSelector((state) => 
    state.systemNotification.isLoading
  );

  // Load notification list
  const loadNotificationList = useCallback(
    (params = {}) => {
      const requestParams = {
        page: params.page || 1,
        limit: params.limit || 20,
      };
      dispatch(getSystemNotificationListRequest(requestParams));
    },
    [dispatch]
  );

  useEffect(() => {
    loadNotificationList({});
  }, [loadNotificationList]);

  const handleTableChange = (tablePagination, filters, sorter) => {
    loadNotificationList({
      page: tablePagination.current,
      limit: tablePagination.pageSize,
    });
  };

  const handleDeleteAll = () => {
    Modal.confirm({
      title: "Xác nhận xóa tất cả thông báo",
      icon: <ExclamationCircleOutlined />,
      content: "Bạn có chắc chắn muốn xóa tất cả thông báo hệ thống? Hành động này không thể hoàn tác.",
      okText: "Xóa tất cả",
      okType: "danger",
      cancelText: "Hủy",
      onOk: () => {
        dispatch(deleteAllSystemNotificationsRequest())
          .then((result) => {
            const deletedCount = result?.payload?.deletedNotifications || 0;
            const deletedJobs = result?.payload?.deletedJobs || 0;
            
            Helper.toastr(
              "Xóa thành công",
              `Đã xóa ${deletedCount} thông báo và ${deletedJobs} tác vụ liên quan`,
              "success"
            );
            
            // Reload the list after deletion
            loadNotificationList({});
          })
          .catch((error) => {
            console.error("Delete all notifications error:", error);
            Helper.toastr(
              "Xóa thất bại",
              "Có lỗi xảy ra khi xóa thông báo. Vui lòng thử lại.",
              "error"
            );
          });
      },
    });
  };

  const handleRetry = useCallback(
    (notificationId) => {
      Modal.confirm({
        title: "Xác nhận gửi lại",
        icon: <ExclamationCircleOutlined />,
        content: "Bạn có chắc chắn muốn gửi lại thông báo cho các thiết bị thất bại?",
        okText: "Gửi lại",
        cancelText: "Hủy",
        onOk: () => {
          dispatch(retrySystemNotificationRequest(notificationId))
            .then(() => {
              Helper.toastr("Thành công", "Đã bắt đầu gửi lại thông báo", "success");
              loadNotificationList({ page: pagination.page, limit: pagination.limit });
            })
            .catch(() => {
              Helper.toastr("Thất bại", "Có lỗi xảy ra khi gửi lại. Vui lòng thử lại.", "error");
            });
        },
      });
    },
    [dispatch, loadNotificationList, pagination]
  );

  const columns = SystemNotificationColumn({
    onRefresh: () => loadNotificationList(),
    pagination: pagination,
    onRetry: handleRetry,
  });

  return (
    <div className="system-notification-list-wrapper">
      <Row className="system-notification-list-header" justify="space-between" align="middle">
        <Row align="middle">
          <Typography.Title level={4}>Thông báo hệ thống</Typography.Title>
        </Row>
        <Space>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="large"
            onClick={handleDeleteAll}
            disabled={notificationList.length === 0}
          >
            Xóa tất cả
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            style={{ width: 150 }}
            onClick={() => navigate("/system_notifications/create")}
          >
            Tạo thông báo
          </Button>
        </Space>
      </Row>
      <div className="system-notification-list-content">
        <Table
          columns={columns}
          dataSource={notificationList}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} thông báo`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1600 }}
          locale={{ emptyText: "Không có thông báo nào." }}
        />
      </div>
    </div>
  );
};

export default SystemNotificationList; 